// Global Instructions Rule Applied!
//
// Configuration → Connect an Agent (all users, NOT admin-only).
//
// The three steps to connecting an AI assistant to Reservoir. This is the one
// agent surface a user needs BEFORE they have a grant, which is exactly why it
// cannot live on the My Agents screen: that screen's menu item carries
// visibility_rule 'has_connected_agent', so it does not exist for the person
// who most needs these instructions (owner 2026-09-02).
//
// Nothing here is hardcoded. The connector URL is env-aware cfg
// (cfg.app_config group 'agent', key 'gateway_connector_url'), so UAT and PROD
// each serve their own gateway host and an environment with no row says so
// rather than confidently handing out the wrong one. The per-assistant
// instructions come from cfg.agent_client_guides via AgentClientGuides —
// supporting a third assistant is an INSERT, not a release.
//
// THE POLL. A grant is born on the gateway's OAuth consent page, which is
// served by the gateway and not by this app, so nothing in React observes it
// appearing. Without the poll the reward for finishing the flow is an unchanged
// menu until the next login. So while this panel is open it watches the user's
// own grant list, and on the first NEW grant it re-fetches /global-configs —
// which recomputes has_connected_agent per request — so the My Agents item
// appears where the last step just said it would.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Typography } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { CopyButton } from '../../../core/components/CopyButton.tsx'
import { HelpButton } from '../../../core/components/HelpDrawer/HelpButton.tsx'
import { AgentClientGuides } from '../../agent_access/components/AgentClientGuides.tsx'
import { fetchMyAgentGrants } from '../../agent_access/services/agentAccessService.ts'
import { AgentAccessConfigSchema } from '../../../state_manager/data_models/agent_access_model.ts'
import type { GlobalConfigsState } from '../../../state_manager/interfaces/global_configs.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

const { Text, Paragraph } = Typography

// Long enough to cover a real consent round trip (sign-in, tick the
// capabilities, pick an expiry), short enough that an abandoned tab stops
// asking. The menu still catches up at the next config poll or login.
const POLL_INTERVAL_MS = 5000
const POLL_CEILING_MS = 5 * 60 * 1000

export default function ConnectAgentPanel(): React.JSX.Element {
  const dispatch = useDispatch()
  const rawAgentAccess = useSelector(
    (state: { globalConfigs: GlobalConfigsState }) => state.globalConfigs?.agent_access
  )
  const authUser = useSelector(
    (state: { auth?: { user?: { id?: string; email?: string } } }) => state.auth?.user
  )

  // Parse-with-defaults, matching MyAgentsTab: a missing or partial cfg payload
  // degrades to "not configured" rather than throwing.
  const agentAccess = useMemo(
    () => AgentAccessConfigSchema.parse(rawAgentAccess ?? {}),
    [rawAgentAccess]
  )
  const connectorUrl = agentAccess.connection.connector_url

  // Grant ids present when the panel opened. Anything outside this set is a
  // connection the user just made.
  const knownGrantIds = useRef<Set<string> | null>(null)
  const [connected, setConnected] = useState(false)
  const userId = authUser?.id
  const userEmail = authUser?.email

  const announce = useCallback(
    (agentName: string) => {
      setConnected(true)
      toast.success(`${agentName} is connected. It now appears under My Agents.`)
      if (userId && userEmail) {
        // Re-GET /global-configs so has_connected_agent flips and the nav item
        // renders. No cache reload: the flag is resolved per request and was
        // never in the backend's shared AppState cache.
        void (dispatch as (action: unknown) => Promise<unknown>)(
          fetchGlobalConfigsThunk({ id: userId, email: userEmail })
        )
      }
    },
    [dispatch, userId, userEmail]
  )

  useEffect(() => {
    if (connected) return
    let cancelled = false
    const startedAt = Date.now()

    const tick = async (): Promise<void> => {
      if (cancelled) return
      // A hidden tab is not worth a round trip; the next visible tick catches up.
      if (typeof document !== 'undefined' && document.hidden) return
      try {
        const res = await fetchMyAgentGrants(getAccessToken(store.getState().auth))
        if (cancelled || res.error) return
        const ids = new Set(res.data.map((g) => g.agent_grants__id))
        if (knownGrantIds.current === null) {
          // First pass only records the baseline — a grant that already existed
          // is not something the user just did.
          knownGrantIds.current = ids
          return
        }
        const fresh = res.data.find((g) => !knownGrantIds.current!.has(g.agent_grants__id))
        if (fresh) {
          knownGrantIds.current = ids
          announce(fresh.agent_grants__agent_name || 'Your agent')
        }
      } catch {
        // A failed poll is not worth surfacing — this is a convenience, and the
        // menu catches up at the next login regardless.
      }
    }

    void tick()
    const timer = setInterval(() => {
      if (Date.now() - startedAt > POLL_CEILING_MS) {
        clearInterval(timer)
        return
      }
      void tick()
    }, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [announce, connected])

  return (
    <div className={`${CONFIG_PANEL_SCROLL_CLASS} flex flex-col gap-2`}>
      {connected && (
        <Alert
          showIcon
          type='success'
          message='Connected'
          description='Your agent is connected. It now appears under Fund Management → My Agents, where you can see what it does and change what it may do.'
        />
      )}

      <FormSection
        title='Step 1 — copy your connection URL'
        titleColor='blue'
        scrollable={false}
        headerActions={
          <HelpButton articleKey='agent_access.my_agents' variant='section' label='Read the guide' />
        }
      >
        {connectorUrl ? (
          <>
            <Text type='secondary' style={{ fontSize: 12 }}>
              Paste this into your AI assistant to connect it to Reservoir. There is no key or
              password to enter — signing in with Microsoft is what identifies you.
            </Text>
            {/* Capped width, not full-bleed. The help drawer is 480px and
                non-modal, so a full-width row would put Copy underneath it —
                and Step 1 of the guide tells the reader to press exactly that
                button. A connector URL is short (the PROD one is ~36 chars),
                so capping the row keeps Copy clear of the drawer at normal
                widths instead of hiding it behind the instructions for using
                it. A narrow window can still overlap — that is inherent to a
                right-hand drawer — but the common case now works. */}
            <div className='mt-2 flex max-w-md items-center gap-2'>
              <code className='min-w-0 flex-1 truncate rounded border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-700'>
                {connectorUrl}
              </code>
              <CopyButton value={connectorUrl} successMessage='Connection URL copied' />
            </div>
          </>
        ) : (
          <Text type='secondary' style={{ fontSize: 12 }}>
            Connector URL not configured for this environment — contact your administrator.
          </Text>
        )}
      </FormSection>

      <FormSection title='Step 2 — add it in your assistant' titleColor='blue' scrollable={false}>
        {/* Same component the in-app guide renders in its Step 2 section. It
            stays registered there too (ui_configs/helpSections.ts) — this is a
            second mount of one cfg-driven block, not a fork of it. */}
        <AgentClientGuides />
      </FormSection>

      <FormSection
        title='Step 3 — sign in and choose what it may do'
        titleColor='blue'
        scrollable={false}
      >
        <Paragraph style={{ fontSize: 12, marginBottom: 8 }} type='secondary'>
          Your assistant sends you to the usual Microsoft sign-in — the same account you use for
          Reservoir. Sign in with your own account, never someone else&rsquo;s: this is what tells
          Reservoir which person the agent is acting for, and the agent inherits your access and
          nobody else&rsquo;s.
        </Paragraph>
        <Paragraph style={{ fontSize: 12, marginBottom: 8 }} type='secondary'>
          Reservoir then shows a consent page. Tick what the agent may read, set how long access
          should last, and decide how writing works — <Text strong>Ask me first</Text>, where the
          agent drafts and nothing is saved until you approve it, or{' '}
          <Text strong>Create without asking</Text>. Start with <Text strong>Ask me first</Text>{' '}
          until you are comfortable with what the agent produces.
        </Paragraph>
        <Paragraph style={{ fontSize: 12, marginBottom: 0 }} type='secondary'>
          Once it is connected, it appears under <Text strong>Fund Management → My Agents</Text>,
          where you can review everything it has done and change what it may do without
          reconnecting.
        </Paragraph>
      </FormSection>
    </div>
  )
}
